import { StepValidationState } from "projects/reg-hub-client/src/interfaces/step";
import { SteppedComponent } from "projects/reg-hub-client/src/interfaces/stepped-component";
import { eOrderType, Order, OrderManagerService, Party, PartyForCreation, PartyRepositoryService, UIConfigurationService, ValidationProblem, ValidationService } from "reg-hub-common";
import { BehaviorSubject, concat, concatMap, forkJoin, map, Observable, switchMap, tap, timer } from "rxjs";

export abstract class PartiesComponent extends SteppedComponent {
    parties$ = new BehaviorSubject<Party[]>([]);

    protected isEditable: boolean = true;

    protected newParty: Party | null = null;

    constructor(
        private partyRepo: PartyRepositoryService,
        private uiConfigurationService: UIConfigurationService,
        orderManager: OrderManagerService,
        validationService: ValidationService) {
        super(orderManager, validationService);
    }

    protected abstract getParties(): Party[];

    protected override init(order: Order): void {
        super.init(order);

        this.parties$.next(this.getParties());
    }

    public override onSaving(): void {

    }

    public override pushErrors(errors: ValidationProblem[] | undefined): void {

    }

    getUIConfiguration() {
        this.uiConfigurationService.getOrderUIConfiguration(this.order)
            .subscribe(config => this.uiConfiguration = config);
    }

    handlePartyAddedEvent(addedParty: Party) {
        this.createParty(addedParty).subscribe(party => {
            this.newParty = party;
        })
    }

    handlePartySavedEvent(savedParty: Party) {
        if (!savedParty.id) {
            // if we don't have an ID yet, create the Party
            this.createParty(savedParty).subscribe();
        } else {
            // otherwise, the Party already exists, so we should update it
            this.updateParty(savedParty);
        }
    }

    handleExistingPartySaveEvent(existingParty: PartyForCreation) {
        this.partyRepo.createNewParty(`orders/${this.order.id}/parties`, existingParty)
            .subscribe(result => {
                const party = ((result as any).resource) as Party;
                this.order.parties = this.order.parties!.concat([party]);
                this.orderManager.updateOrder(this.order);
                this.parties$.next(this.getParties());
            });
    }

    handleExistingLinkedPartySaveEvent(existingParties: PartyForCreation[]) {
        // First Party = Parent Party
        let parentParty = existingParties.shift()!;

        // This creates the parent party, then creates all the child parties while linking them to the parent party in parallel
        // in order to avoid having one show up on the list, then another, then their links -- this way they all show up at once
        this.partyRepo.createNewParty(`orders/${this.order.id}/parties`, parentParty)
            .pipe(map(createdParentPartyResponse => createdParentPartyResponse.resource),
                concatMap(createdParentParty => {
                return forkJoin(existingParties.map(p => this.partyRepo.createNewParty(`orders/${this.order.id}/parties`, p)
                        .pipe(concatMap(childPartyResponse => this.partyRepo.linkParties(createdParentParty.id, childPartyResponse.resource.id)
                            .pipe(tap(partyLink => createdParentParty.linkedParties?.push(partyLink)),
                                map(() => childPartyResponse.resource))))))
                    .pipe(map(createdChildParties => [ createdParentParty, ...createdChildParties ]));
            }))
            .subscribe(parties => {
                this.order.parties = [...(this.order.parties ?? []), ...parties];
                this.orderManager.updateOrder(this.order);
                this.parties$.next(this.getParties());
            });
    }

    handlePartyCopiedEvent(copiedParty: Party) {
        // create a full copy of the party so we don't overwrite the party from which we copied
        const copiedPartyWithoutId = { ...copiedParty };
        delete (copiedPartyWithoutId.originalEntityID);
        this.handlePartyAddedEvent(copiedPartyWithoutId);
    }

    handlePartyRemovedEvent(removedParty: Party) {
        if (removedParty.id) {
            this.partyRepo.deleteParty(`orders/${this.order.id}/parties/${removedParty.id}`)
                .subscribe(() => this.handleDeletedParty(removedParty));
        } else {
            this.handleDeletedParty(removedParty);
        }
    }

    handlePartyFormValueChangedEvent(party: Party) {
        let orderToValidate = this.copyOrder(this.order);

        if (orderToValidate.orderTypeID === eOrderType.landTitleSearch) {
            if (orderToValidate.landTitleSearchCriteria) {
                orderToValidate.landTitleSearchCriteria.party = party;
            }
        } else if (orderToValidate.orderTypeID === eOrderType.knowYourCustomer) {
            if (orderToValidate.knowYourCustomerCriteria) {
                orderToValidate.knowYourCustomerCriteria.party = party;
            }
        } else {
            orderToValidate.parties = [party];
        }

        this.validate(orderToValidate);
    }

    private handleDeletedParty(removedParty: Party) {
        const index = this.order.parties!.findIndex(obj => obj.id === removedParty.id || !obj.id);
        const linkedToParty = this.order.parties!.find(p => p.linkedParties?.some(lp => lp.linkedPartyID === removedParty.id));

        if (linkedToParty) {
            const indexOfLink = linkedToParty.linkedParties!.findIndex(lp => lp.linkedPartyID === removedParty.id);
            linkedToParty.linkedParties?.splice(indexOfLink, 1);
        }

        // as long as our index was found 
        if (index != -1) {
            this.order.parties!.splice(index, 1);
            this.orderManager.updateOrder(this.order);
            this.parties$.next(this.getParties());
        }
    }

    protected createParty(party: Party): Observable<Party> {
        this.orderManager.savingParty$.next(true);

        return this.partyRepo.createParty(`orders/${this.order.id}/parties`, party)
            .pipe(
                map(result => ((result as any).resource) as Party),
                tap(party => {
                    this.order.parties!.push(party);
                    this.orderManager.updateOrder(this.order);
                    this.parties$.next(this.getParties());
                    this.orderManager.savingParty$.next(false);
                }));
    }

    protected updateParty(party: Party) {
        this.orderManager.savingParty$.next(true);

        this.partyRepo.updateParty(`orders/${this.order.id}/parties/${party.id}`, party)
            .subscribe(result => {
                const party = ((result as any).resource) as Party;
                this.replaceParty(party);
                this.orderManager.savingParty$.next(false);
            });
    }

    protected replaceParty(party: Party) {
        const index = this.order.parties!.findIndex(obj => obj.id === party.id);

        // as long as our index was found 
        if (index != -1) {
            this.order.parties![index] = party;
            this.orderManager.updateOrder(this.order);
            this.parties$.next(this.getParties());
        }
    }

    protected static parseErrorsForValidationState(errors: ValidationProblem[] | undefined, order: Order, filterPartyType: (party: Party) => boolean): StepValidationState {
        const filteredParties = order.parties?.filter(party => filterPartyType(party));

        if (filteredParties?.length === 0) {
            return StepValidationState.ValidationError;
        }

        const partyValidationErrors = errors?.filter(err => err.path.startsWith('parties'));

        return partyValidationErrors?.some(err => {
            const partyIndex = Number(err.path.split('/').at(1));

            if (Number.isNaN(partyIndex) || partyIndex < 0) {
                return false;
            }

            const party = order.parties?.at(partyIndex);

            if (!party) {
                return false;
            }

            return filterPartyType(party);
        }) ? StepValidationState.ValidationError : StepValidationState.ValidationSuccess;
    }
}